import type {
  ProjectAction,
  PullTestErrorLogEntry,
  PullTestEventCategory,
  PullTestPilingInstallation,
  PullTestingInstallationFieldKey,
  PullTestPilingInstallationFields,
  PullTestingEventsLogQuery,
  PullTestingProjectActionDetails,
} from './project';
import {
  PULL_TEST_INSTALLATION_FIELD_ORDER,
  pullTestInstallationFieldBlueprint,
  pullTestPilingInstallationFields,
} from './project';
import { formatFeetInchesFromDecimalFeet } from './pullTestingFeetInches';
import {
  buildPullTestResultsDisplaySection,
  PULL_TEST_RESULTS_MODALITY_SPECS,
  type PullTestResultsDisplaySection,
  type PullTestResultsModalityKey,
} from './pullTestResultsDisplay.util';

/** Server-written row when offline replay overwrites existing install/test data. */
export const PULL_TEST_OFFLINE_SYNC_COLLISION_ACTION_TYPE =
  'pull_test_offline_sync_collision' as const;

/** Mutation audit appended when a PM resolves a collision — history only; not an inbox row. */
export const PULL_TEST_OFFLINE_SYNC_COLLISION_RESOLVED_ACTION_TYPE =
  'pull_test_offline_sync_collision_resolved' as const;

export const PULL_TEST_EVENT_CATEGORY_ALL_TESTS: readonly PullTestEventCategory[] = [
  'tensile_test_completed',
  'lateral_test_completed',
  'compression_test_completed',
];

export const PULL_TEST_EVENT_CATEGORY_MODALITY_COMPLETED = 'modality_test_completed' as const;

export const PULL_TEST_TENSILE_COMPLETED_ACTION_TYPE = 'pull_test_tensile_completed' as const;
export const PULL_TEST_LATERAL_COMPLETED_ACTION_TYPE = 'pull_test_lateral_completed' as const;
export const PULL_TEST_COMPRESSION_COMPLETED_ACTION_TYPE =
  'pull_test_compression_completed' as const;
export const PULL_TEST_SOAK_BYPASS_ALERT_CONFIRMED_ACTION_TYPE =
  'pull_test_soak_bypass_alert_confirmed' as const;
export const PULL_TEST_PILING_INSTALLED_ACTION_TYPE = 'pull_test_piling_installed' as const;
export const PULL_TEST_EVENT_CATEGORY_PILING_INSTALLED = 'piling_installed' as const;

export const PULL_TEST_MODALITY_COMPLETION_ACTION_TYPES = [
  PULL_TEST_TENSILE_COMPLETED_ACTION_TYPE,
  PULL_TEST_LATERAL_COMPLETED_ACTION_TYPE,
  PULL_TEST_COMPRESSION_COMPLETED_ACTION_TYPE,
] as const;

/** Log page tabs mapped to events-log API filters. */
export type PullTestingLogTab =
  | 'ALL'
  | 'INSTALLS'
  | 'TENSILE'
  | 'LATERAL'
  | 'COMPRESSION'
  | 'TESTS'
  | 'SOAK'
  | 'ERRORS'
  | 'CONFLICTS';

export type OfflineSyncCollisionKind = PullTestOfflineSyncCollisionKind;

export type OfflineSyncResolutionStatus = PullTestOfflineSyncCollisionResolutionStatus;

export type CollisionOfficialSubmission = 'server' | 'offline_replay';

export interface ResolveCollisionRequest {
  officialSubmission: CollisionOfficialSubmission;
  reassignOtherSubmissionToPilingID?: string;
  notes?: string;
}

export interface ResolveCollisionResponseData {
  projectID: string;
  collisionId: string;
  pilingID: string;
  officialSubmission: CollisionOfficialSubmission;
  reassignOtherSubmissionToPilingID?: string;
  resolutionStatus: OfflineSyncResolutionStatus;
  pullTestPiling?: Record<string, unknown>;
  reassignedPullTestPiling?: Record<string, unknown>;
}

/** Per-submission action in the collision detail modal (before save). */
export type PullTestCollisionSubmissionAction = 'keep' | 'discard' | 'assign';

export type PullTestCollisionSubmissionSide = 'server' | 'conflicting';

export interface PullTestCollisionResolveDraft {
  serverAction: PullTestCollisionSubmissionAction | null;
  conflictingAction: PullTestCollisionSubmissionAction | null;
  serverAssignToPilingID?: string;
  conflictingAssignToPilingID?: string;
}

export function pullTestCollisionResolveDraftValidationMessage(
  draft: PullTestCollisionResolveDraft | null,
  collisionPilingId: string | null | undefined
): string | null {
  if (!draft?.serverAction || !draft?.conflictingAction) {
    return null;
  }

  const actions = [draft.serverAction, draft.conflictingAction];
  const keepCount = actions.filter(action => action === 'keep').length;
  const discardCount = actions.filter(action => action === 'discard').length;
  const assignCount = actions.filter(action => action === 'assign').length;
  const collisionId = collisionPilingId?.trim() ?? '';

  if (keepCount > 1) {
    return 'Only one submission can be kept on this piling.';
  }
  if (discardCount === 2) {
    return 'Both submissions cannot be discarded.';
  }
  if (assignCount === 2) {
    return 'Only one submission can be assigned to another piling.';
  }
  if (keepCount === 0) {
    return 'One submission must be kept on this piling.';
  }

  if (draft.serverAction === 'assign') {
    const target = draft.serverAssignToPilingID?.trim() ?? '';
    if (!target) {
      return 'Enter the piling ID for the server submission.';
    }
    if (collisionId && target === collisionId) {
      return 'Assign target must differ from the collision piling.';
    }
  }

  if (draft.conflictingAction === 'assign') {
    const target = draft.conflictingAssignToPilingID?.trim() ?? '';
    if (!target) {
      return 'Enter the piling ID for the conflicting submission.';
    }
    if (collisionId && target === collisionId) {
      return 'Assign target must differ from the collision piling.';
    }
  }

  return null;
}

const PULL_TEST_COLLISION_RESOLVE_DRAFT_ACTION_HINTS = new Set([
  'Only one submission can be kept on this piling.',
  'One submission must be kept on this piling.',
  'Both submissions cannot be discarded.',
  'Only one submission can be assigned to another piling.',
]);

/** Action-pairing hint for the collision modal — excludes assign-field messages (Save stays disabled). */
export function pullTestCollisionResolveDraftActionHint(
  draft: PullTestCollisionResolveDraft | null,
  collisionPilingId: string | null | undefined
): string | null {
  const message = pullTestCollisionResolveDraftValidationMessage(draft, collisionPilingId);
  if (!message || !PULL_TEST_COLLISION_RESOLVE_DRAFT_ACTION_HINTS.has(message)) {
    return null;
  }
  return message;
}

export function pullTestCollisionResolveDraftIsValid(
  draft: PullTestCollisionResolveDraft | null,
  collisionPilingId: string | null | undefined
): boolean {
  if (!draft?.serverAction || !draft?.conflictingAction) {
    return false;
  }
  return pullTestCollisionResolveDraftValidationMessage(draft, collisionPilingId) === null;
}

export function pullTestCollisionResolveDraftToRequest(
  draft: PullTestCollisionResolveDraft
): ResolveCollisionRequest {
  if (draft.serverAction === 'keep' && draft.conflictingAction === 'discard') {
    return { officialSubmission: 'server' };
  }
  if (draft.serverAction === 'keep' && draft.conflictingAction === 'assign') {
    return {
      officialSubmission: 'server',
      reassignOtherSubmissionToPilingID: draft.conflictingAssignToPilingID!.trim(),
    };
  }
  if (draft.serverAction === 'discard' && draft.conflictingAction === 'keep') {
    return { officialSubmission: 'offline_replay' };
  }
  if (draft.serverAction === 'assign' && draft.conflictingAction === 'keep') {
    return {
      officialSubmission: 'offline_replay',
      reassignOtherSubmissionToPilingID: draft.serverAssignToPilingID!.trim(),
    };
  }
  throw new Error('Invalid collision resolve draft');
}

export type PullTestOfflineSyncCollisionKind =
  | 'installation'
  | 'tensile_test'
  | 'lateral_test'
  | 'compression_test'
  | 'soak_bypass';

export type PullTestOfflineSyncCollisionResolutionStatus =
  | 'pending_review'
  | 'resolved'
  | 'acknowledged'
  | 'restored_previous'
  | string;

/** All event categories used on collision overlap rows. */
export const PULL_TEST_EVENT_LOG_COLLISION_EVENT_CATEGORIES: readonly PullTestEventCategory[] = [
  'install_performed',
  'soak_bypass',
  'tensile_test_performed',
  'lateral_test_performed',
  'compression_test_performed',
  'offline_sync_collision',
  'offline_sync_collision_resolved',
];

/** Standalone client validation audits accepted by the events-log API `actionType` filter. */
export const PULL_TEST_EVENTS_LOG_VALIDATION_ACTION_TYPES = [
  'pull_test_deflection_gauge_misalignment',
  'pull_test_applied_load_exceeds_next_target',
  'pull_test_max_deflection_limit_exceeded',
  'pull_test_empty_load_step_skipped',
  'pull_test_monotonic_deflection_override',
  'pull_test_validation_alert_dismissed',
] as const;

/**
 * Fixed `actionType` strings that may appear in events-log responses
 * (default feed + explicit fetches; excludes legacy coarse PATCH audits and
 * stored-only `pull_test_offline_sync_collision_resolved`).
 */
export const PULL_TESTING_EVENTS_LOG_ACTION_TYPES = [
  ...PULL_TEST_EVENTS_LOG_VALIDATION_ACTION_TYPES,
  PULL_TEST_PILING_INSTALLED_ACTION_TYPE,
  ...PULL_TEST_MODALITY_COMPLETION_ACTION_TYPES,
  PULL_TEST_SOAK_BYPASS_ALERT_CONFIRMED_ACTION_TYPE,
  PULL_TEST_OFFLINE_SYNC_COLLISION_ACTION_TYPE,
] as const;

export type PullTestEventsLogValidationActionType =
  (typeof PULL_TEST_EVENTS_LOG_VALIDATION_ACTION_TYPES)[number];

export type PullTestingEventsLogActionType = (typeof PULL_TESTING_EVENTS_LOG_ACTION_TYPES)[number];

export type PullTestEventsLogRowKind =
  | 'validation'
  | 'modality_completion'
  | 'piling_installed'
  | 'collision'
  | 'collision_resolved';

/** Comma-separated `actionType` query value for validation / ERRORS tab. */
export function pullTestEventsLogValidationActionTypeFilter(): string {
  return PULL_TEST_EVENTS_LOG_VALIDATION_ACTION_TYPES.join(',');
}

/** Companion `*Name` field for a recognized user id field on events-log rows. */
export function pullTestEventsLogCompanionNameField(idFieldKey: string): string | null {
  switch (idFieldKey) {
    case 'clientUserId':
      return 'clientUserName';
    case 'updatedBy':
      return 'updatedByName';
    case 'soakBypassedBy':
      return 'soakBypassedByName';
    case 'userId':
      return 'userName';
    default:
      if (idFieldKey.endsWith('ByID')) {
        return `${idFieldKey.slice(0, -4)}ByName`;
      }
      return null;
  }
}

export function pullTestEventsLogIsUserIdField(key: string): boolean {
  return pullTestEventsLogCompanionNameField(key) != null;
}

/** Skip when flattening — the paired id field is rendered with the resolved name. */
export function pullTestEventsLogIsCompanionUserNameField(key: string): boolean {
  return key === 'userName' || key === 'clientUserName' || key.endsWith('ByName');
}

/**
 * Resolve a user id to a display name using companion `*Name` fields, `userDisplayNames`,
 * and the row actor `userName` when the id matches top-level `userId`.
 */
export function pullTestEventsLogResolveUserDisplayName(
  entry: Pick<PullTestErrorLogEntry, 'userId' | 'userName' | 'userDisplayNames'>,
  userId: string | null | undefined,
  context?: Record<string, unknown> | null,
  idFieldKey?: string
): string {
  const id = userId?.trim();
  if (!id) {
    return 'Unknown user';
  }

  if (context && idFieldKey) {
    const nameKey = pullTestEventsLogCompanionNameField(idFieldKey);
    const companion = nameKey ? context[nameKey] : undefined;
    if (typeof companion === 'string' && companion.trim()) {
      return companion.trim();
    }
  }

  const fromMap = entry.userDisplayNames?.[id]?.trim();
  if (fromMap) {
    return fromMap;
  }

  if (id === entry.userId?.trim()) {
    const actorName = entry.userName?.trim();
    if (actorName) {
      return actorName;
    }
  }

  return id;
}

/** Display name for the row actor (`userId`). */
export function pullTestEventsLogActorDisplayName(
  entry: Pick<PullTestErrorLogEntry, 'userId' | 'userName' | 'userDisplayNames'>
): string {
  return pullTestEventsLogResolveUserDisplayName(entry, entry.userId, entry, 'userId');
}

/** Human-readable timestamp for events-log detail and collision compare views. */
export function formatPullTestEventsLogDisplayTimestamp(raw: string | null | undefined): string {
  if (!raw?.trim()) {
    return '—';
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw.trim();
  }
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const PULL_TEST_SOAK_BYPASS_SNAPSHOT_TIMESTAMP_KEYS = [
  'soakBypassedAt',
  'soakBypassTimestamp',
  'soakBypassedTimestamp',
  'updatedAt',
] as const;

function pullTestSoakBypassSnapshotTimestamp(
  entry: Pick<PullTestErrorLogEntry, 'actionTimestamp' | 'actionDetails'>,
  snapshot: Record<string, unknown>,
  submissionSide: 'server' | 'conflicting'
): string | null {
  for (const key of PULL_TEST_SOAK_BYPASS_SNAPSHOT_TIMESTAMP_KEYS) {
    const value = snapshot[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  if (submissionSide !== 'conflicting') {
    return null;
  }

  const details = entry.actionDetails as Record<string, unknown> | undefined;
  const offlineMutationCreatedAt = details?.['offlineMutationCreatedAt'];
  if (typeof offlineMutationCreatedAt === 'string' && offlineMutationCreatedAt.trim()) {
    return offlineMutationCreatedAt.trim();
  }
  const appliedAt = details?.['appliedAt'];
  if (typeof appliedAt === 'string' && appliedAt.trim()) {
    return appliedAt.trim();
  }
  const actionTimestamp = entry.actionTimestamp?.trim();
  return actionTimestamp || null;
}

function pullTestSoakBypassSnapshotActorId(
  entry: Pick<PullTestErrorLogEntry, 'userId'>,
  snapshot: Record<string, unknown>,
  submissionSide: 'server' | 'conflicting'
): string | null {
  const fromSnapshot = snapshot['soakBypassedBy'];
  if (typeof fromSnapshot === 'string' && fromSnapshot.trim()) {
    return fromSnapshot.trim();
  }
  if (submissionSide === 'conflicting') {
    const actorId = entry.userId?.trim();
    return actorId || null;
  }
  return null;
}

/** Scalar compare rows for soak bypass collisions — actor and time first so PMs can distinguish submissions. */
export function pullTestSoakBypassCollisionCompareRows(
  entry: Pick<
    PullTestErrorLogEntry,
    'userId' | 'userName' | 'userDisplayNames' | 'actionTimestamp' | 'actionDetails'
  >,
  snapshot: Record<string, unknown>,
  submissionSide: 'server' | 'conflicting'
): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  const actorId = pullTestSoakBypassSnapshotActorId(entry, snapshot, submissionSide);
  rows.push({
    label: 'Performed by',
    value: actorId
      ? pullTestEventsLogResolveUserDisplayName(entry, actorId, snapshot, 'soakBypassedBy')
      : '—',
  });

  const timestamp = pullTestSoakBypassSnapshotTimestamp(entry, snapshot, submissionSide);
  rows.push({
    label: 'Performed at',
    value: timestamp ? formatPullTestEventsLogDisplayTimestamp(timestamp) : '—',
  });

  if (snapshot['soakBypassed'] === true) {
    rows.push({ label: 'Soak bypassed', value: 'Yes' });
  }

  return rows;
}

/** Event type values for the event log filter dropdown. */
export type PullTestingEventLogEventTypeFilter =
  | 'all'
  | 'validation_alert'
  | 'piling_installed'
  | 'tensile_test_completed'
  | 'lateral_test_completed'
  | 'compression_test_completed'
  | 'soak_bypass_confirmed'
  | 'collisions';

export type PullTestingEventLogConflictStatusFilter = 'pending' | 'resolved' | 'all';

/** Draft/applied filter state for the pull testing event log page. */
export interface PullTestingEventLogFilterValues {
  eventType: PullTestingEventLogEventTypeFilter;
  /** Local `yyyy-MM-dd` for `<input type="date">`. */
  startDate: string;
  endDate: string;
  pilingId: string;
  /** Resolved display name substring; empty means all users. */
  userName: string;
  /** When {@link PullTestingEventLogFilterValues.eventType} is `collisions`, maps to API `collisionStatus`. */
  conflictStatus: PullTestingEventLogConflictStatusFilter;
}

export const PULL_TESTING_EVENT_LOG_DEFAULT_LOOKBACK_DAYS = 7;

/** Query param when opening the event log from the dashboard pending-collision badge. */
export const PULL_TESTING_EVENT_LOG_FOCUS_PENDING_COLLISIONS = 'pending-collisions' as const;

export const PULL_TESTING_EVENT_LOG_EVENT_TYPE_OPTIONS: readonly {
  value: PullTestingEventLogEventTypeFilter;
  label: string;
}[] = [
  { value: 'all', label: 'All events' },
  { value: 'validation_alert', label: 'Validation errors' },
  { value: 'piling_installed', label: 'Piling installed' },
  { value: 'tensile_test_completed', label: 'Tensile completed' },
  { value: 'lateral_test_completed', label: 'Lateral completed' },
  { value: 'compression_test_completed', label: 'Compression completed' },
  { value: 'soak_bypass_confirmed', label: 'Soak bypass confirmed' },
  { value: 'collisions', label: 'Piling collision' },
];

export function formatPullTestingEventLogDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${formatPullTestingEventLogDateInputPad2(m)}-${formatPullTestingEventLogDateInputPad2(day)}`;
}

function formatPullTestingEventLogDateInputPad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Default filter values — last {@link PULL_TESTING_EVENT_LOG_DEFAULT_LOOKBACK_DAYS} days, all types. */
export function createDefaultPullTestingEventLogFilters(
  referenceDate: Date = new Date()
): PullTestingEventLogFilterValues {
  const end = new Date(referenceDate);
  const start = new Date(referenceDate);
  start.setDate(start.getDate() - PULL_TESTING_EVENT_LOG_DEFAULT_LOOKBACK_DAYS);
  return {
    eventType: 'all',
    startDate: formatPullTestingEventLogDateInput(start),
    endDate: formatPullTestingEventLogDateInput(end),
    pilingId: '',
    userName: '',
    conflictStatus: 'pending',
  };
}

/** Filters for dashboard deep-link — pending piling collisions, no date window (matches badge probe). */
export function createPendingCollisionsEventLogFilters(): PullTestingEventLogFilterValues {
  return {
    eventType: 'collisions',
    startDate: '',
    endDate: '',
    pilingId: '',
    userName: '',
    conflictStatus: 'pending',
  };
}

export function isPendingCollisionsEventLogFocus(focus: string | null | undefined): boolean {
  return focus?.trim() === PULL_TESTING_EVENT_LOG_FOCUS_PENDING_COLLISIONS;
}

/** Maps event log filter dropdown to `eventCategory` and/or `actionType` query params. */
export function pullTestEventsLogQueryForEventTypeFilter(
  filter: PullTestingEventLogEventTypeFilter
): { eventCategory?: string; actionType?: string } {
  switch (filter) {
    case 'all':
      return {};
    case 'validation_alert':
      return pullTestEventsLogQueryForTab('ERRORS');
    case 'piling_installed':
      return pullTestEventsLogQueryForTab('INSTALLS');
    case 'tensile_test_completed':
      return pullTestEventsLogQueryForTab('TENSILE');
    case 'lateral_test_completed':
      return pullTestEventsLogQueryForTab('LATERAL');
    case 'compression_test_completed':
      return pullTestEventsLogQueryForTab('COMPRESSION');
    case 'soak_bypass_confirmed':
      return pullTestEventsLogQueryForTab('SOAK');
    case 'collisions':
      return pullTestEventsLogQueryForTab('CONFLICTS');
    default:
      return {};
  }
}

/** Converts local calendar date inputs to inclusive UTC ISO bounds for the events-log API. */
export function pullTestEventsLogDateRangeQuery(
  startDate: string,
  endDate: string
): { startDate?: string; endDate?: string } {
  const result: { startDate?: string; endDate?: string } = {};
  const start = startDate.trim();
  const end = endDate.trim();
  if (start) {
    result.startDate = pullTestEventsLogDateInputToApiStartIso(start);
  }
  if (end) {
    result.endDate = pullTestEventsLogDateInputToApiEndIso(end);
  }
  return result;
}

function pullTestEventsLogDateInputToApiStartIso(yyyyMmDd: string): string {
  const parts = yyyyMmDd.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

function pullTestEventsLogDateInputToApiEndIso(yyyyMmDd: string): string {
  const parts = yyyyMmDd.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

/** Maps a log tab to `eventCategory` and/or `actionType` query params. */
export function pullTestEventsLogQueryForTab(
  tab: PullTestingLogTab
): { eventCategory?: string; actionType?: string } {
  switch (tab) {
    case 'INSTALLS':
      return { actionType: PULL_TEST_PILING_INSTALLED_ACTION_TYPE };
    case 'TENSILE':
      return { actionType: PULL_TEST_TENSILE_COMPLETED_ACTION_TYPE };
    case 'LATERAL':
      return { actionType: PULL_TEST_LATERAL_COMPLETED_ACTION_TYPE };
    case 'COMPRESSION':
      return { actionType: PULL_TEST_COMPRESSION_COMPLETED_ACTION_TYPE };
    case 'TESTS':
      return { eventCategory: PULL_TEST_EVENT_CATEGORY_MODALITY_COMPLETED };
    case 'SOAK':
      return { actionType: PULL_TEST_SOAK_BYPASS_ALERT_CONFIRMED_ACTION_TYPE };
    case 'ERRORS':
      return { actionType: pullTestEventsLogValidationActionTypeFilter() };
    case 'CONFLICTS':
      return { actionType: PULL_TEST_OFFLINE_SYNC_COLLISION_ACTION_TYPE };
    case 'ALL':
      return {};
    default:
      return {};
  }
}

/** Merges top-level and actionDetails event category arrays from a log row. */
export function pullTestEventCategoriesForEntry(entry: PullTestErrorLogEntry): PullTestEventCategory[] {
  const fromRow = Array.isArray(entry.eventCategories) ? entry.eventCategories : [];
  const details = entry.actionDetails as Record<string, unknown> | undefined;
  const eventCategories = details?.['eventCategories'];
  const fromDetails = Array.isArray(eventCategories)
    ? (eventCategories as PullTestEventCategory[])
    : [];
  const merged = [...fromRow, ...fromDetails].filter(
    (c): c is PullTestEventCategory => typeof c === 'string' && c.length > 0
  );
  return [...new Set(merged)];
}

export function pullTestEventCategoryLabel(category: PullTestEventCategory): string {
  switch (category) {
    case 'install_performed':
      return 'Install conflict';
    case 'tensile_test_performed':
      return 'Tensile conflict';
    case 'lateral_test_performed':
      return 'Lateral conflict';
    case 'compression_test_performed':
      return 'Compression conflict';
    case 'modality_test_completed':
      return 'Test completed';
    case 'tensile_test_completed':
      return 'Tensile completed';
    case 'lateral_test_completed':
      return 'Lateral completed';
    case 'compression_test_completed':
      return 'Compression completed';
    case 'piling_installed':
      return 'Piling installed';
    case 'soak_bypass':
      return 'Soak bypass conflict';
    case 'offline_sync_collision':
      return 'Sync conflict';
    case 'offline_sync_collision_resolved':
      return 'Conflict resolved';
    case 'validation_alert':
      return 'Validation';
    default: {
      const fallback = category as string;
      return fallback.replace(/_/g, ' ');
    }
  }
}

/** Maps a log row to the event-type filter value used in the dropdown. */
export function pullTestEventsLogEventTypeFilterForEntry(
  entry: PullTestErrorLogEntry
): Exclude<PullTestingEventLogEventTypeFilter, 'all'> | null {
  const actionType = entry.actionType?.trim() ?? '';

  if (isPullTestOfflineSyncCollisionEntry(entry)) {
    return 'collisions';
  }

  if (isPullTestModalityCompletionEntry(entry)) {
    switch (actionType) {
      case PULL_TEST_TENSILE_COMPLETED_ACTION_TYPE:
        return 'tensile_test_completed';
      case PULL_TEST_LATERAL_COMPLETED_ACTION_TYPE:
        return 'lateral_test_completed';
      case PULL_TEST_COMPRESSION_COMPLETED_ACTION_TYPE:
        return 'compression_test_completed';
      default: {
        const categories = pullTestEventCategoriesForEntry(entry);
        if (categories.includes('tensile_test_completed')) {
          return 'tensile_test_completed';
        }
        if (categories.includes('lateral_test_completed')) {
          return 'lateral_test_completed';
        }
        if (categories.includes('compression_test_completed')) {
          return 'compression_test_completed';
        }
        return null;
      }
    }
  }

  if (actionType === PULL_TEST_SOAK_BYPASS_ALERT_CONFIRMED_ACTION_TYPE) {
    return 'soak_bypass_confirmed';
  }

  if (isPullTestPilingInstalledEntry(entry)) {
    return 'piling_installed';
  }

  if (isPullTestValidationEventsLogEntry(entry)) {
    return 'validation_alert';
  }

  return null;
}

function pullTestEventsLogFilterLabelForValue(
  filter: Exclude<PullTestingEventLogEventTypeFilter, 'all'>
): string | null {
  const option = PULL_TESTING_EVENT_LOG_EVENT_TYPE_OPTIONS.find(opt => opt.value === filter);
  return option?.label ?? null;
}

/** Badge label for list cards — matches the event type filter dropdown 1:1. */
export function pullTestEventsLogFilterLabelForEntry(entry: PullTestErrorLogEntry): string {
  const filter = pullTestEventsLogEventTypeFilterForEntry(entry);
  if (filter) {
    const label = pullTestEventsLogFilterLabelForValue(filter);
    if (label) {
      return label;
    }
  }
  return pullTestEventsLogEntrySummary(entry);
}

/** @deprecated Use {@link pullTestEventsLogFilterLabelForEntry} for list card badges. */
export function pullTestEventsLogPrimaryCategoryLabel(entry: PullTestErrorLogEntry): string {
  return pullTestEventsLogFilterLabelForEntry(entry);
}

export function isPullTestPilingInstalledEntry(
  entry: Pick<ProjectAction, 'actionType' | 'previousState' | 'newState'>
): boolean {
  const actionType = entry.actionType?.trim() ?? '';
  if (actionType === PULL_TEST_PILING_INSTALLED_ACTION_TYPE) {
    return true;
  }
  const previousState = entry.previousState as Record<string, unknown> | undefined;
  const newState = entry.newState as Record<string, unknown> | undefined;
  return (
    previousState?.['kind'] === 'pullTesting_piling_installed' ||
    newState?.['kind'] === 'pullTesting_piling_installed'
  );
}

export function pullTestPilingInstalledSnapshot(
  state: Record<string, unknown> | undefined | null
): Record<string, unknown> | null {
  if (!state || state['kind'] !== 'pullTesting_piling_installed') {
    return null;
  }
  const snapshot = state['snapshot'];
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return null;
  }
  return snapshot as Record<string, unknown>;
}

export function pullTestModalityCompletionSnapshot(
  state: Record<string, unknown> | undefined | null
): Record<string, unknown> | null {
  if (!state || state['kind'] !== 'pullTesting_modality_completion') {
    return null;
  }
  const snapshot = state['snapshot'];
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return null;
  }
  return snapshot as Record<string, unknown>;
}

/** Modality key for a modality-completion events-log row. */
export function pullTestModalityCompletionModalityKey(
  entry: Pick<ProjectAction, 'actionType' | 'previousState' | 'newState'>
): PullTestResultsModalityKey | null {
  const actionType = entry.actionType?.trim() ?? '';
  switch (actionType) {
    case PULL_TEST_TENSILE_COMPLETED_ACTION_TYPE:
      return 'tensile';
    case PULL_TEST_LATERAL_COMPLETED_ACTION_TYPE:
      return 'lateral';
    case PULL_TEST_COMPRESSION_COMPLETED_ACTION_TYPE:
      return 'compression';
    default:
      break;
  }

  for (const state of [entry.newState, entry.previousState]) {
    if (!state || typeof state !== 'object' || Array.isArray(state)) {
      continue;
    }
    const record = state as Record<string, unknown>;
    const modality = record['modality'];
    if (modality === 'tensile' || modality === 'lateral' || modality === 'compression') {
      return modality;
    }
    const resultsKey = record['resultsKey'];
    if (typeof resultsKey === 'string') {
      const spec = PULL_TEST_RESULTS_MODALITY_SPECS.find(s => s.resultsKey === resultsKey);
      if (spec) {
        return spec.key;
      }
    }
  }

  return null;
}

function pullTestModalityCompletionResultsRecord(
  record: Record<string, unknown> | null | undefined,
  modality: PullTestResultsModalityKey
): Record<string, unknown> | null {
  if (!record) {
    return null;
  }
  const spec = PULL_TEST_RESULTS_MODALITY_SPECS.find(s => s.key === modality);
  if (!spec) {
    return null;
  }

  const nested = record[spec.resultsKey];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }

  if (Array.isArray(record['steps']) || typeof record['status'] === 'string') {
    return record;
  }

  return null;
}

/** Completed modality `*TestResults` blob from a modality-completion events-log row. */
export function pullTestModalityCompletionResultsBlob(
  entry: PullTestErrorLogEntry
): { modality: PullTestResultsModalityKey; results: Record<string, unknown> } | null {
  const modality = pullTestModalityCompletionModalityKey(entry);
  if (!modality) {
    return null;
  }

  for (const state of [entry.newState, entry.previousState]) {
    const snapshot = pullTestModalityCompletionSnapshot(
      state as Record<string, unknown> | undefined
    );
    const results = pullTestModalityCompletionResultsRecord(snapshot, modality);
    if (results) {
      return { modality, results };
    }
  }

  const details = (entry.actionDetails as PullTestingProjectActionDetails | undefined)?.details;
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    const results = pullTestModalityCompletionResultsRecord(details as Record<string, unknown>, modality);
    if (results) {
      return { modality, results };
    }
  }

  return null;
}

export interface PullTestEventsLogDetailLabelValue {
  label: string;
  value: string;
  fullWidth?: boolean;
}

export interface PullTestEventsLogInstallationDisplay {
  contextRows: PullTestEventsLogDetailLabelValue[];
  installedBy?: string;
  installedAt?: string;
  actualEmbedmentDepth?: string;
  scalarRows: PullTestEventsLogDetailLabelValue[];
  driveTimesSecByFoot: number[];
}

function pullTestEventsLogInstallationScalarValue(
  fields: PullTestPilingInstallationFields,
  key: PullTestingInstallationFieldKey
): string | null {
  switch (key) {
    case 'pushToDepth': {
      const pushed = fields.pushedToDepthFt;
      if (typeof pushed !== 'number' || !Number.isFinite(pushed)) {
        return null;
      }
      return formatFeetInchesFromDecimalFeet(pushed);
    }
    case 'actualEmbedmentDepth': {
      const emb = fields.actualEmbedmentDepthFt;
      if (emb === undefined || emb === null || !Number.isFinite(Number(emb))) {
        return null;
      }
      return formatFeetInchesFromDecimalFeet(Number(emb));
    }
    case 'northSouth': {
      const deg = fields.northSouthAngleDeg;
      if (typeof deg !== 'number' || !Number.isFinite(deg)) {
        return null;
      }
      return `${deg} deg`;
    }
    case 'eastWest': {
      const deg = fields.eastWestAngleDeg;
      if (typeof deg !== 'number' || !Number.isFinite(deg)) {
        return null;
      }
      return `${deg} deg`;
    }
    case 'comments': {
      const comments = fields.comments;
      if (typeof comments !== 'string' || !comments.trim()) {
        return null;
      }
      return comments.trim();
    }
    default:
      return null;
  }
}

/** Dashboard-style test results section for modality-completion log rows. */
export function buildPullTestEventsLogModalityTestSection(
  entry: PullTestErrorLogEntry,
  formatTimestamp: (iso: string) => string
): PullTestResultsDisplaySection | null {
  const blob = pullTestModalityCompletionResultsBlob(entry);
  if (!blob) {
    return null;
  }

  const spec = PULL_TEST_RESULTS_MODALITY_SPECS.find(s => s.key === blob.modality);
  if (!spec) {
    return null;
  }

  const testByID =
    typeof blob.results['testByID'] === 'string' ? blob.results['testByID'] : undefined;
  const testTimestamp =
    typeof blob.results['testTimestamp'] === 'string' ? blob.results['testTimestamp'] : undefined;

  return buildPullTestResultsDisplaySection(spec, blob.results, {
    testedByName: testByID
      ? pullTestEventsLogResolveUserDisplayName(entry, testByID, blob.results, 'testByID')
      : undefined,
    testTimestampLabel:
      testTimestamp && testTimestamp.trim() ? formatTimestamp(testTimestamp) : undefined,
  });
}

/** Dashboard-style installation section for piling-installed log rows. */
export function buildPullTestEventsLogPilingInstalledDisplay(
  entry: PullTestErrorLogEntry,
  formatTimestamp: (iso: string) => string
): PullTestEventsLogInstallationDisplay | null {
  if (!isPullTestPilingInstalledEntry(entry)) {
    return null;
  }

  const details = (entry.actionDetails as PullTestingProjectActionDetails | undefined)?.details as
    | Record<string, unknown>
    | undefined;
  const before = pullTestPilingInstalledSnapshot(
    entry.previousState as Record<string, unknown> | undefined
  );
  const after = pullTestPilingInstalledSnapshot(
    entry.newState as Record<string, unknown> | undefined
  );

  const contextRows: PullTestEventsLogDetailLabelValue[] = [];
  const enteredSoaking = details?.['enteredSoaking'];
  if (typeof enteredSoaking === 'boolean') {
    contextRows.push({ label: 'Entered soaking', value: enteredSoaking ? 'Yes' : 'No' });
  }
  const soakTimeHours = details?.['soakTimeHours'];
  if (typeof soakTimeHours === 'number' && Number.isFinite(soakTimeHours)) {
    contextRows.push({ label: 'Soak time', value: `${soakTimeHours} hours` });
  }

  const beforeStatus = before?.['status'];
  const afterStatus = after?.['status'];
  if (
    typeof beforeStatus === 'string' &&
    typeof afterStatus === 'string' &&
    beforeStatus !== afterStatus
  ) {
    contextRows.push({ label: 'Status change', value: `${beforeStatus} → ${afterStatus}` });
  } else if (typeof afterStatus === 'string' && afterStatus.trim()) {
    contextRows.push({ label: 'Status', value: afterStatus });
  }

  const installationRaw = after?.['installation'];
  const installation =
    installationRaw && typeof installationRaw === 'object' && !Array.isArray(installationRaw)
      ? (installationRaw as PullTestPilingInstallation)
      : null;
  const fields = pullTestPilingInstallationFields(installation);

  let installedBy: string | undefined;
  const installedByID = installation?.installedByID;
  if (typeof installedByID === 'string' && installedByID.trim()) {
    installedBy = pullTestEventsLogResolveUserDisplayName(
      entry,
      installedByID,
      installation as Record<string, unknown>,
      'installedByID'
    );
  }

  let installedAt: string | undefined;
  const installedTimestamp = installation?.installedTimestamp ?? after?.['installedAt'];
  if (typeof installedTimestamp === 'string' && installedTimestamp.trim()) {
    installedAt = formatTimestamp(installedTimestamp);
  }

  let actualEmbedmentDepth: string | undefined;
  const emb = fields.actualEmbedmentDepthFt;
  if (emb !== undefined && emb !== null && Number.isFinite(Number(emb))) {
    actualEmbedmentDepth = formatFeetInchesFromDecimalFeet(Number(emb));
  }

  const scalarRows: PullTestEventsLogDetailLabelValue[] = [];
  for (const key of PULL_TEST_INSTALLATION_FIELD_ORDER) {
    if (key === 'driveTime' || key === 'actualEmbedmentDepth') {
      continue;
    }
    const formatted = pullTestEventsLogInstallationScalarValue(fields, key);
    if (formatted === null) {
      continue;
    }
    scalarRows.push({
      label: pullTestInstallationFieldBlueprint(key).label,
      value: formatted,
      fullWidth: key === 'comments',
    });
  }

  const driveRaw = fields.driveTimeSecByFoot;
  const driveTimesSecByFoot = Array.isArray(driveRaw)
    ? driveRaw.filter((x): x is number => typeof x === 'number' && !Number.isNaN(x))
    : [];

  if (
    contextRows.length === 0 &&
    !installedBy &&
    !installedAt &&
    !actualEmbedmentDepth &&
    scalarRows.length === 0 &&
    driveTimesSecByFoot.length === 0
  ) {
    return null;
  }

  return {
    contextRows,
    installedBy,
    installedAt,
    actualEmbedmentDepth,
    scalarRows,
    driveTimesSecByFoot,
  };
}

export function isPullTestModalityCompletionEntry(
  entry: Pick<ProjectAction, 'actionType' | 'previousState'>
): boolean {
  const actionType = entry.actionType?.trim() ?? '';
  if ((PULL_TEST_MODALITY_COMPLETION_ACTION_TYPES as readonly string[]).includes(actionType)) {
    return true;
  }
  const previousState = entry.previousState as Record<string, unknown> | undefined;
  return previousState?.['kind'] === 'pullTesting_modality_completion';
}

export function pullTestEventsLogRowKind(entry: PullTestErrorLogEntry): PullTestEventsLogRowKind {
  const actionType = entry.actionType?.trim() ?? '';
  if (actionType === PULL_TEST_OFFLINE_SYNC_COLLISION_ACTION_TYPE) {
    return 'collision';
  }
  if (actionType === PULL_TEST_OFFLINE_SYNC_COLLISION_RESOLVED_ACTION_TYPE) {
    return 'collision_resolved';
  }
  if (isPullTestModalityCompletionEntry(entry)) {
    return 'modality_completion';
  }
  if (isPullTestPilingInstalledEntry(entry)) {
    return 'piling_installed';
  }
  const previousState = entry.previousState as Record<string, unknown> | undefined;
  if (
    isPullTestValidationEventsLogEntry(entry) ||
    previousState?.['kind'] === 'pullTesting_standalone_audit'
  ) {
    return 'validation';
  }
  return 'validation';
}

export function pullTestEventsLogRowKindLabel(kind: PullTestEventsLogRowKind): string {
  switch (kind) {
    case 'collision':
      return 'Sync conflict';
    case 'collision_resolved':
      return 'Conflict resolved';
    case 'modality_completion':
      return 'Test completed';
    case 'piling_installed':
      return 'Piling installed';
    case 'validation':
      return 'Validation';
    default:
      return 'Event';
  }
}

function pullTestModalityCompletionBadgeClass(entry: PullTestErrorLogEntry): string | null {
  const categories = pullTestEventCategoriesForEntry(entry);
  if (
    categories.includes('tensile_test_completed') ||
    entry.actionType?.trim() === PULL_TEST_TENSILE_COMPLETED_ACTION_TYPE
  ) {
    return 'pt-el-log-type-tensile';
  }
  if (
    categories.includes('lateral_test_completed') ||
    entry.actionType?.trim() === PULL_TEST_LATERAL_COMPLETED_ACTION_TYPE
  ) {
    return 'pt-el-log-type-lateral';
  }
  if (
    categories.includes('compression_test_completed') ||
    entry.actionType?.trim() === PULL_TEST_COMPRESSION_COMPLETED_ACTION_TYPE
  ) {
    return 'pt-el-log-type-compression';
  }
  if (isPullTestModalityCompletionEntry(entry)) {
    return 'pt-el-log-type-tensile';
  }
  return null;
}

export function pullTestEventsLogBadgeClass(entry: PullTestErrorLogEntry): string {
  const categories = pullTestEventCategoriesForEntry(entry);
  if (categories.includes('offline_sync_collision')) {
    return 'pt-el-log-type-conflict';
  }
  if (categories.includes('validation_alert') || pullTestEventsLogRowKind(entry) === 'validation') {
    return 'pt-el-log-type-error';
  }
  const modalityBadge = pullTestModalityCompletionBadgeClass(entry);
  if (modalityBadge) {
    return modalityBadge;
  }
  if (isPullTestPilingInstalledEntry(entry) || categories.includes('piling_installed')) {
    return 'pt-el-log-type-install';
  }
  if (categories.includes('install_performed')) {
    return 'pt-el-log-type-install';
  }
  if (categories.includes('tensile_test_performed')) {
    return 'pt-el-log-type-tensile';
  }
  if (categories.includes('lateral_test_performed')) {
    return 'pt-el-log-type-lateral';
  }
  if (categories.includes('compression_test_performed')) {
    return 'pt-el-log-type-compression';
  }
  if (
    categories.includes('soak_bypass') ||
    entry.actionType?.trim() === PULL_TEST_SOAK_BYPASS_ALERT_CONFIRMED_ACTION_TYPE
  ) {
    return 'pt-el-log-type-soak';
  }
  if (pullTestEventsLogRowKind(entry) === 'collision_resolved') {
    return 'pt-el-log-type-update';
  }
  return 'pt-el-log-type-update';
}

export function isPullTestOfflineSyncCollisionEntry(
  entry: Pick<ProjectAction, 'actionType'>
): boolean {
  return entry.actionType?.trim() === PULL_TEST_OFFLINE_SYNC_COLLISION_ACTION_TYPE;
}

/** Resolve audit row — do not use for inbox, resolve button, or pending/resolved state. */
export function isPullTestOfflineSyncCollisionResolveAuditEntry(
  entry: Pick<ProjectAction, 'actionType'>
): boolean {
  return entry.actionType?.trim() === PULL_TEST_OFFLINE_SYNC_COLLISION_RESOLVED_ACTION_TYPE;
}

export function isCollisionRow(entry: Pick<ProjectAction, 'actionType'>): boolean {
  return isPullTestOfflineSyncCollisionEntry(entry);
}

export function isPendingOfflineSyncCollision(entry: PullTestErrorLogEntry): boolean {
  return (
    isPullTestOfflineSyncCollisionEntry(entry) &&
    pullTestOfflineSyncCollisionResolutionStatus(entry) === 'pending_review'
  );
}

export function isPendingCollision(entry: PullTestErrorLogEntry): boolean {
  return isPendingOfflineSyncCollision(entry);
}

export function isResolvedOfflineSyncCollision(entry: PullTestErrorLogEntry): boolean {
  if (!isPullTestOfflineSyncCollisionEntry(entry)) {
    return false;
  }
  const status = pullTestOfflineSyncCollisionResolutionStatus(entry);
  if (status === 'resolved') {
    return true;
  }
  // Legacy rows before unified `resolved` status (do not infer from audit rows).
  return status === 'acknowledged' || status === 'restored_previous';
}

export function canResolveOfflineSyncCollision(entry: PullTestErrorLogEntry): boolean {
  return isPendingOfflineSyncCollision(entry);
}

/** Excludes collision resolve audit rows from event-log inbox lists. */
export function pullTestEventsLogShouldDisplayInboxEntry(
  entry: PullTestErrorLogEntry
): boolean {
  return !isPullTestOfflineSyncCollisionResolveAuditEntry(entry);
}

/** List-card conflict status aligned with events-log `collisionStatus` filter values. */
export function pullTestEventsLogCollisionListStatus(
  entry: PullTestErrorLogEntry
): 'pending_review' | 'resolved' | null {
  if (!isPullTestOfflineSyncCollisionEntry(entry)) {
    return null;
  }
  if (isPendingOfflineSyncCollision(entry)) {
    return 'pending_review';
  }
  if (isResolvedOfflineSyncCollision(entry)) {
    return 'resolved';
  }
  return null;
}

export function pullTestEventsLogCollisionStatusLabel(
  status: 'pending_review' | 'resolved'
): string {
  return status === 'pending_review' ? 'Pending review' : 'Resolved';
}

export function pullTestEventsLogCollisionStatusBadgeClass(
  entry: PullTestErrorLogEntry
): string | null {
  const status = pullTestEventsLogCollisionListStatus(entry);
  if (!status) {
    return null;
  }
  return status === 'pending_review'
    ? 'pt-el-conflict-status-pending'
    : 'pt-el-conflict-status-resolved';
}

/** Maps conflict status filter to events-log `collisionStatus` (collision scope only). */
export function pullTestEventsLogCollisionStatusQuery(
  eventType: PullTestingEventLogEventTypeFilter,
  conflictStatus: PullTestingEventLogConflictStatusFilter
): { collisionStatus?: 'pending_review' | 'resolved' } {
  if (eventType !== 'collisions' || conflictStatus === 'all') {
    return {};
  }
  return {
    collisionStatus: conflictStatus === 'pending' ? 'pending_review' : 'resolved',
  };
}

/** Lightweight probe for pending offline sync collisions (dashboard event-log badge). */
export function pullTestEventsLogPendingCollisionsProbeQuery(): Pick<
  PullTestingEventsLogQuery,
  'actionType' | 'collisionStatus' | 'limit'
> {
  return {
    actionType: PULL_TEST_OFFLINE_SYNC_COLLISION_ACTION_TYPE,
    collisionStatus: 'pending_review',
    limit: 1,
  };
}

export function pullTestCollisionId(entry: PullTestErrorLogEntry): string | null {
  if (typeof entry.collisionId === 'string' && entry.collisionId.trim().length > 0) {
    return entry.collisionId.trim();
  }
  const details = entry.actionDetails as Record<string, unknown> | undefined;
  const fromDetails = details?.['collisionId'];
  return typeof fromDetails === 'string' && fromDetails.trim().length > 0
    ? fromDetails.trim()
    : null;
}

export function pullTestCollisionOfficialSubmission(
  entry: PullTestErrorLogEntry
): CollisionOfficialSubmission | null {
  const details = entry.actionDetails as Record<string, unknown> | undefined;
  const official = details?.['officialSubmission'];
  if (official === 'server' || official === 'offline_replay') {
    return official;
  }
  return null;
}

export function pullTestCollisionReassignTarget(entry: PullTestErrorLogEntry): string | null {
  const details = entry.actionDetails as Record<string, unknown> | undefined;
  const target = details?.['reassignOtherSubmissionToPilingID'];
  return typeof target === 'string' && target.trim() ? target.trim() : null;
}

export type PullTestCollisionSubmissionOutcome = 'kept' | 'discarded' | 'assigned';

export interface PullTestCollisionResolvedSubmissionHeadline {
  outcome: PullTestCollisionSubmissionOutcome;
  label: string;
}

/** Per-column headline for a resolved collision (kept / discarded / assigned elsewhere). */
export function pullTestCollisionResolvedSubmissionHeadline(
  entry: PullTestErrorLogEntry,
  side: PullTestCollisionSubmissionSide
): PullTestCollisionResolvedSubmissionHeadline | null {
  if (!isPullTestOfflineSyncCollisionEntry(entry) || isPendingOfflineSyncCollision(entry)) {
    return null;
  }

  const collisionPilingId = entry.pilingID?.trim() || 'this piling';
  const reassignTarget = pullTestCollisionReassignTarget(entry);
  let official = pullTestCollisionOfficialSubmission(entry);

  if (!official) {
    const status = pullTestOfflineSyncCollisionResolutionStatus(entry);
    if (status === 'acknowledged') {
      official = 'offline_replay';
    } else if (status === 'restored_previous') {
      official = 'server';
    }
  }

  if (!official) {
    return null;
  }

  const serverKept = official === 'server';

  if (side === 'server') {
    if (serverKept) {
      return { outcome: 'kept', label: `Kept on ${collisionPilingId}` };
    }
    if (reassignTarget) {
      return { outcome: 'assigned', label: `Assigned to ${reassignTarget}` };
    }
    return { outcome: 'discarded', label: 'Discarded' };
  }

  if (!serverKept) {
    return { outcome: 'kept', label: `Kept on ${collisionPilingId}` };
  }
  if (reassignTarget) {
    return { outcome: 'assigned', label: `Assigned to ${reassignTarget}` };
  }
  return { outcome: 'discarded', label: 'Discarded' };
}

export function pullTestOfflineSyncCollisionKindLabel(
  kind: PullTestOfflineSyncCollisionKind | null | undefined
): string {
  switch (kind) {
    case 'installation':
      return 'Installation conflict';
    case 'tensile_test':
      return 'Tensile test conflict';
    case 'lateral_test':
      return 'Lateral test conflict';
    case 'compression_test':
      return 'Compression test conflict';
    case 'soak_bypass':
      return 'Soak bypass conflict';
    default:
      return 'Offline sync conflict';
  }
}

export function isPullTestValidationEventsLogEntry(
  entry: Pick<ProjectAction, 'actionType' | 'previousState'> & {
    eventCategories?: PullTestEventCategory[];
    actionDetails?: unknown;
  }
): boolean {
  const categories = pullTestEventCategoriesForEntry(entry as PullTestErrorLogEntry);
  if (categories.includes('validation_alert')) {
    return true;
  }
  const actionType = entry.actionType?.trim() ?? '';
  if (!actionType) {
    return false;
  }
  if (isPullTestOfflineSyncCollisionEntry(entry)) {
    return false;
  }
  if (isPullTestModalityCompletionEntry(entry)) {
    return false;
  }
  if (isPullTestPilingInstalledEntry(entry)) {
    return false;
  }
  if (actionType === PULL_TEST_SOAK_BYPASS_ALERT_CONFIRMED_ACTION_TYPE) {
    return false;
  }
  if (
    (PULL_TEST_EVENTS_LOG_VALIDATION_ACTION_TYPES as readonly string[]).includes(actionType)
  ) {
    return true;
  }
  const previousState = entry.previousState as Record<string, unknown> | undefined;
  if (previousState?.['kind'] === 'pullTesting_standalone_audit') {
    return actionType.startsWith('pull_test_');
  }
  return false;
}

export function pullTestOfflineSyncCollisionSnapshot(
  state: Record<string, unknown> | undefined | null
): Record<string, unknown> | null {
  if (!state || state['kind'] !== 'pullTesting_offline_sync_collision') {
    return null;
  }
  const snapshot = state['snapshot'];
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return null;
  }
  return snapshot as Record<string, unknown>;
}

/** Full piling document when state is a collision/piling-installed wrapper or a raw snapshot. */
export function pullTestEventsLogPilingSnapshot(
  state: Record<string, unknown> | undefined | null
): Record<string, unknown> | null {
  if (!state || typeof state !== 'object' || Array.isArray(state)) {
    return null;
  }
  const collision = pullTestOfflineSyncCollisionSnapshot(state);
  if (collision) {
    return collision;
  }
  const installed = pullTestPilingInstalledSnapshot(state);
  if (installed) {
    return installed;
  }
  if (typeof state['pullTestPilingID'] === 'string' || typeof state['status'] === 'string') {
    return state;
  }
  return null;
}

export function collisionSnapshots(entry: PullTestErrorLogEntry): {
  serverBefore: Record<string, unknown>;
  operatorAfter: Record<string, unknown>;
} | null {
  const serverBefore = pullTestOfflineSyncCollisionSnapshot(
    entry.previousState as Record<string, unknown> | undefined
  );
  const operatorAfter = pullTestOfflineSyncCollisionSnapshot(
    entry.newState as Record<string, unknown> | undefined
  );
  if (!serverBefore || !operatorAfter) {
    return null;
  }
  return { serverBefore, operatorAfter };
}

export function pullTestOfflineSyncCollisionKind(
  entry: PullTestErrorLogEntry
): PullTestOfflineSyncCollisionKind | null {
  const details = entry.actionDetails as Record<string, unknown> | undefined;
  const kind = details?.['collisionKind'];
  return typeof kind === 'string' ? (kind as PullTestOfflineSyncCollisionKind) : null;
}

export function pullTestOfflineSyncCollisionResolutionStatus(
  entry: PullTestErrorLogEntry
): PullTestOfflineSyncCollisionResolutionStatus | null {
  const details = entry.actionDetails as Record<string, unknown> | undefined;
  const status = details?.['resolutionStatus'];
  return typeof status === 'string' ? status : null;
}

/** Summary line for list cards — category-aware. */
export function pullTestEventsLogEntrySummary(entry: PullTestErrorLogEntry): string {
  if (isPullTestOfflineSyncCollisionEntry(entry)) {
    return pullTestOfflineSyncCollisionKindLabel(pullTestOfflineSyncCollisionKind(entry));
  }
  if (isPullTestModalityCompletionEntry(entry)) {
    const actionType = entry.actionType?.trim() ?? '';
    switch (actionType) {
      case PULL_TEST_TENSILE_COMPLETED_ACTION_TYPE:
        return 'Tensile completed';
      case PULL_TEST_LATERAL_COMPLETED_ACTION_TYPE:
        return 'Lateral completed';
      case PULL_TEST_COMPRESSION_COMPLETED_ACTION_TYPE:
        return 'Compression completed';
      default:
        return 'Test completed';
    }
  }
  if (entry.actionType?.trim() === PULL_TEST_SOAK_BYPASS_ALERT_CONFIRMED_ACTION_TYPE) {
    return 'Soak bypass confirmed';
  }
  if (isPullTestPilingInstalledEntry(entry)) {
    return 'Piling installed';
  }
  const categories = pullTestEventCategoriesForEntry(entry);
  if (categories.length > 0) {
    return categories.map(pullTestEventCategoryLabel).join(' · ');
  }
  const actionType = entry.actionType?.trim();
  if (!actionType) {
    return 'Event';
  }
  if (actionType.startsWith('pull_test_')) {
    return actionType
      .slice('pull_test_'.length)
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return actionType
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
